import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import ShubhBottomNav from './ShubhBottomNav';
import { ChevronLeft, Info, HelpCircle } from 'lucide-react';
import '../../styles/shubh-design-system.css';

import { BACKEND_URL } from '../../lib/backend';

const API_URL = BACKEND_URL;

// Planet data with symbols and colors
const PLANETS = [
  { id: 'sun', name: 'Sun', hindi: 'सूर्य', symbol: '☉', color: '#FFB74D', bgColor: '#FFF3E0' },
  { id: 'moon', name: 'Moon', hindi: 'चंद्र', symbol: '☽', color: '#90CAF9', bgColor: '#E3F2FD' },
  { id: 'mars', name: 'Mars', hindi: 'मंगल', symbol: '♂', color: '#EF5350', bgColor: '#FFEBEE' },
  { id: 'mercury', name: 'Mercury', hindi: 'बुध', symbol: '☿', color: '#81C784', bgColor: '#E8F5E9' },
  { id: 'jupiter', name: 'Jupiter', hindi: 'बृहस्पति', symbol: '♃', color: '#F5B942', bgColor: '#FFF8E1' },
  { id: 'venus', name: 'Venus', hindi: 'शुक्र', symbol: '♀', color: '#F06292', bgColor: '#FCE4EC' },
  { id: 'saturn', name: 'Saturn', hindi: 'शनि', symbol: '♄', color: '#7986CB', bgColor: '#E8EAF6' },
  { id: 'rahu', name: 'Rahu', hindi: 'राहु', symbol: '☊', color: '#78909C', bgColor: '#ECEFF1' },
  { id: 'ketu', name: 'Ketu', hindi: 'केतु', symbol: '☋', color: '#A1887F', bgColor: '#EFEBE9' },
];

const ShubhPlanetaryPositionsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  const [loading, setLoading] = useState(true);
  const [planetData, setPlanetData] = useState([]);

  useEffect(() => {
    // Simulated planet positions - replace with API call
    setTimeout(() => {
      setPlanetData([
        { id: 'sun', sign: 'Sagittarius', signHindi: 'धनु', degree: '15° 23\' 45"', house: 12, nakshatra: 'Moola', pada: 2, retro: false },
        { id: 'moon', sign: 'Leo', signHindi: 'सिंह', degree: '08° 45\' 12"', house: 5, nakshatra: 'Magha', pada: 3, retro: false },
        { id: 'mars', sign: 'Libra', signHindi: 'तुला', degree: '22° 18\' 33"', house: 7, nakshatra: 'Vishakha', pada: 1, retro: false },
        { id: 'mercury', sign: 'Capricorn', signHindi: 'मकर', degree: '03° 56\' 08"', house: 9, nakshatra: 'Uttara Ashadha', pada: 4, retro: true },
        { id: 'jupiter', sign: 'Taurus', signHindi: 'वृषभ', degree: '28° 12\' 55"', house: 4, nakshatra: 'Mrigashira', pada: 1, retro: false },
        { id: 'venus', sign: 'Taurus', signHindi: 'वृषभ', degree: '11° 34\' 22"', house: 4, nakshatra: 'Rohini', pada: 2, retro: false },
        { id: 'saturn', sign: 'Aquarius', signHindi: 'कुंभ', degree: '19° 08\' 41"', house: 11, nakshatra: 'Shatabhisha', pada: 4, retro: true },
        { id: 'rahu', sign: 'Libra', signHindi: 'तुला', degree: '05° 23\' 17"', house: 7, nakshatra: 'Chitra', pada: 3, retro: true },
        { id: 'ketu', sign: 'Aries', signHindi: 'मेष', degree: '05° 23\' 17"', house: 1, nakshatra: 'Ashwini', pada: 2, retro: true },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getPlanetInfo = (id) => PLANETS.find(p => p.id === id);

  return (
    <div className="shubh-page" style={{ background: 'var(--shubh-ivory)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-20 px-4 py-3"
        style={{ 
          background: 'var(--shubh-ivory)',
          borderBottom: '1px solid var(--border-light)',
          paddingTop: 'calc(12px + env(safe-area-inset-top))'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/janam-kundli')}
              className="p-2 rounded-full"
              style={{ background: 'var(--warm-beige)' }}
            >
              <ChevronLeft className="w-5 h-5" style={{ color: 'var(--ink-dark)' }} />
            </button>
            <h1 
              className="text-lg font-medium"
              style={{ fontFamily: 'var(--font-display)', color: 'var(--ink-dark)' }}
            >
              {isHindi ? 'ग्रह स्थिति' : 'Planetary Positions'}
            </h1>
          </div>
          <button 
            className="p-2 rounded-full"
            style={{ background: 'var(--warm-beige)' }}
          >
            <HelpCircle className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
          </button>
        </div>
      </header>

      <div className="shubh-page-content" style={{ paddingTop: 'var(--space-4)' }}>
        {/* Table Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-12 gap-2 px-3 py-2 mb-2"
        >
          <div className="col-span-3 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
            {isHindi ? 'ग्रह' : 'Planet'}
          </div>
          <div className="col-span-3 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
            {isHindi ? 'राशि' : 'Sign'}
          </div>
          <div className="col-span-3 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
            {isHindi ? 'अंश' : 'Degree'}
          </div>
          <div className="col-span-3 text-xs font-medium text-right" style={{ color: 'var(--ink-muted)' }}>
            {isHindi ? 'नक्षत्र' : 'Nakshatra'}
          </div>
        </motion.div>

        {/* Planet Rows */}
        <div className="space-y-2">
          {loading ? (
            Array(9).fill(0).map((_, i) => (
              <div key={i} className="shubh-skeleton h-16 rounded-xl" />
            ))
          ) : (
            planetData.map((planet, index) => {
              const info = getPlanetInfo(planet.id);
              return (
                <motion.div
                  key={planet.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="shubh-card p-3"
                  onClick={() => navigate(`/kundli/planet/${planet.id}`)}
                >
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Planet */}
                    <div className="col-span-3 flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                        style={{ background: info?.bgColor, color: info?.color }}
                      >
                        {info?.symbol}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
                          {isHindi ? info?.hindi : info?.name}
                        </p>
                        {planet.retro && (
                          <span 
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(239, 83, 80, 0.1)', color: '#EF5350' }}
                          >
                            {isHindi ? 'वक्री' : 'Retro'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sign */}
                    <div className="col-span-3">
                      <p className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
                        {isHindi ? planet.signHindi : planet.sign}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                        {isHindi ? `भाव ${planet.house}` : `House ${planet.house}`}
                      </p>
                    </div>

                    {/* Degree */}
                    <div className="col-span-3">
                      <p className="text-sm font-mono" style={{ color: 'var(--ink-medium)' }}>
                        {planet.degree}
                      </p>
                    </div>

                    {/* Nakshatra */}
                    <div className="col-span-3 text-right">
                      <p className="text-sm" style={{ color: 'var(--ink-dark)' }}>
                        {planet.nakshatra}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                        {isHindi ? `पाद ${planet.pada}` : `Pada ${planet.pada}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 p-4 rounded-xl"
          style={{ background: 'var(--warm-beige)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
              {isHindi ? 'जानकारी' : 'Legend'}
            </span>
          </div>
          <p className="text-xs" style={{ color: 'var(--ink-light)' }}>
            {isHindi 
              ? 'वक्री = ग्रह का पीछे की ओर गति। किसी भी ग्रह पर टैप करके विस्तृत व्याख्या देखें।'
              : 'Retro = Planet appears to move backward. Tap any planet to see detailed interpretation.'
            }
          </p>
        </motion.div>
      </div>

      <ShubhBottomNav />
    </div>
  );
};

export default ShubhPlanetaryPositionsPage;
